/**
 * Line-profile sampling with sub-pixel interpolation and perpendicular band
 * integration (the "line width" of ImageJ / LMK line profiles).
 */
import type { Plane } from './types';

export type Interp = 'nearest' | 'bilinear' | 'bicubic';
export type BandStat = 'mean' | 'median' | 'min' | 'max' | 'sum';
export type OobMode = 'nan' | 'clamp';

export interface SamplingSettings {
  interp: Interp;
  /** Distance between samples along the line, in pixels (> 0). */
  step: number;
  stat: BandStat;
  oob: OobMode;
}

export const DEFAULT_SAMPLING: SamplingSettings = { interp: 'bilinear', step: 1, stat: 'mean', oob: 'nan' };

export interface LineGeom {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Band width in pixels (integer >= 1). */
  width: number;
}

export interface Profile {
  n: number;
  /** Distance along the line from the start point, in pixels. */
  t: Float64Array;
  /** Image coordinates of each sample centre. */
  px: Float64Array;
  py: Float64Array;
  /** Band statistic (the profile value). */
  v: Float64Array;
  /** Sample standard deviation across the band (0 for width 1). */
  sd: Float64Array;
  /** Minimum / maximum across the band. */
  lo: Float64Array;
  hi: Float64Array;
  /** Number of valid (finite, in-bounds) band samples. */
  cnt: Uint16Array;
  /** OR of the saturation-mask bits of every pixel contributing to the sample. */
  clip: Uint8Array;
  length: number;
  angleDeg: number;
  bandSamples: number;
}

export function lineLength(g: LineGeom): number {
  return Math.hypot(g.x1 - g.x0, g.y1 - g.y0);
}

/** Line angle in degrees, counter-clockwise from +x with y pointing up on screen. */
export function lineAngleDeg(g: LineGeom): number {
  return (Math.atan2(-(g.y1 - g.y0), g.x1 - g.x0) * 180) / Math.PI;
}

/** Number of band samples across the line (ImageJ: 1-pixel spacing across). */
export function bandCount(width: number): number {
  return Math.max(1, Math.round(width));
}

/** Axis-aligned bounding box (with margin) of the pixels a line profile can touch. */
export function lineBounds(g: LineGeom, margin = 3): { x: number; y: number; w: number; h: number } {
  const half = bandCount(g.width) / 2;
  const x = Math.floor(Math.min(g.x0, g.x1) - half - margin);
  const y = Math.floor(Math.min(g.y0, g.y1) - half - margin);
  const x2 = Math.ceil(Math.max(g.x0, g.x1) + half + margin);
  const y2 = Math.ceil(Math.max(g.y0, g.y1) + half + margin);
  return { x, y, w: x2 - x, h: y2 - y };
}

function keys(t: number): number {
  // Keys cubic convolution kernel, a = -0.5 (Catmull-Rom).
  const a = -0.5;
  const x = Math.abs(t);
  if (x <= 1) return (a + 2) * x * x * x - (a + 3) * x * x + 1;
  if (x < 2) return a * x * x * x - 5 * a * x * x + 8 * a * x - 4 * a;
  return 0;
}

/**
 * Interpolated value of `plane` at continuous image coordinates (x, y).
 * Neighbours outside the plane are replicated from its edge. Returns NaN when
 * the point lies outside the full image and oob = 'nan'.
 */
export function sampleAt(p: Plane, x: number, y: number, interp: Interp, oob: OobMode): number {
  if (!(x >= 0 && y >= 0 && x <= p.fullWidth && y <= p.fullHeight)) {
    if (oob === 'nan' || !Number.isFinite(x) || !Number.isFinite(y)) return NaN;
    x = Math.min(Math.max(x, 0), p.fullWidth);
    y = Math.min(Math.max(y, 0), p.fullHeight);
  }
  const w = p.width;
  const h = p.height;
  const d = p.data;
  const cx = (i: number) => (i < 0 ? 0 : i >= w ? w - 1 : i);
  const cy = (j: number) => (j < 0 ? 0 : j >= h ? h - 1 : j);
  if (interp === 'nearest') {
    return d[cy(Math.floor(y) - p.oy) * w + cx(Math.floor(x) - p.ox)];
  }
  const u = x - 0.5 - p.ox;
  const v = y - 0.5 - p.oy;
  const i0 = Math.floor(u);
  const j0 = Math.floor(v);
  const fx = u - i0;
  const fy = v - j0;
  if (interp === 'bilinear') {
    const a = cx(i0);
    const b = cx(i0 + 1);
    const r0 = cy(j0) * w;
    const r1 = cy(j0 + 1) * w;
    const top = d[r0 + a] + (d[r0 + b] - d[r0 + a]) * fx;
    const bot = d[r1 + a] + (d[r1 + b] - d[r1 + a]) * fx;
    return top + (bot - top) * fy;
  }
  let s = 0;
  for (let m = -1; m <= 2; m++) {
    const wy = keys(m - fy);
    if (wy === 0) continue;
    const row = cy(j0 + m) * w;
    let rs = 0;
    for (let k = -1; k <= 2; k++) rs += d[row + cx(i0 + k)] * keys(k - fx);
    s += rs * wy;
  }
  return s;
}

/** OR of clip-mask bits over the pixels supporting an interpolated sample. */
function clipBits(mask: Uint8Array, W: number, H: number, x: number, y: number, interp: Interp): number {
  if (interp === 'nearest') {
    const i = Math.floor(x);
    const j = Math.floor(y);
    return i >= 0 && j >= 0 && i < W && j < H ? mask[j * W + i] : 0;
  }
  const r = interp === 'bilinear' ? 0 : 1;
  const i0 = Math.floor(x - 0.5);
  const j0 = Math.floor(y - 0.5);
  let bits = 0;
  for (let j = j0 - r; j <= j0 + 1 + r; j++) {
    if (j < 0 || j >= H) continue;
    for (let i = i0 - r; i <= i0 + 1 + r; i++) if (i >= 0 && i < W) bits |= mask[j * W + i];
  }
  return bits;
}

function median(buf: Float64Array, n: number): number {
  // Native numeric sort of a typed-array copy (much faster than Array + comparator).
  const a = buf.slice(0, n).sort();
  const m = n >> 1;
  return n % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
}

export function sampleProfile(plane: Plane, g: LineGeom, s: SamplingSettings, clipMask?: Uint8Array | null): Profile {
  const L = lineLength(g);
  const step = s.step > 0 ? s.step : 1;
  const n = L > 0 ? Math.floor(L / step + 1e-9) + 1 : 1;
  const ux = L > 0 ? (g.x1 - g.x0) / L : 1;
  const uy = L > 0 ? (g.y1 - g.y0) / L : 0;
  // Perpendicular (rotated +90 deg in image coordinates).
  const nx = -uy;
  const ny = ux;
  const m = bandCount(g.width);
  const offs = Array.from({ length: m }, (_, k) => k - (m - 1) / 2);
  const out: Profile = {
    n,
    t: new Float64Array(n),
    px: new Float64Array(n),
    py: new Float64Array(n),
    v: new Float64Array(n),
    sd: new Float64Array(n),
    lo: new Float64Array(n),
    hi: new Float64Array(n),
    cnt: new Uint16Array(n),
    clip: new Uint8Array(n),
    length: L,
    angleDeg: lineAngleDeg(g),
    bandSamples: m,
  };
  const buf = new Float64Array(m);
  const W = plane.fullWidth;
  const H = plane.fullHeight;
  for (let k = 0; k < n; k++) {
    const t = k * step;
    const cx = g.x0 + ux * t;
    const cy = g.y0 + uy * t;
    out.t[k] = t;
    out.px[k] = cx;
    out.py[k] = cy;
    let c = 0;
    let sum = 0;
    let lo = Infinity;
    let hi = -Infinity;
    let bits = 0;
    for (let b = 0; b < m; b++) {
      const x = cx + nx * offs[b];
      const y = cy + ny * offs[b];
      const val = sampleAt(plane, x, y, s.interp, s.oob);
      if (!Number.isFinite(val)) continue;
      if (clipMask) {
        const qx = s.oob === 'clamp' ? Math.min(Math.max(x, 0), W - 1e-9) : x;
        const qy = s.oob === 'clamp' ? Math.min(Math.max(y, 0), H - 1e-9) : y;
        bits |= clipBits(clipMask, W, H, qx, qy, s.interp);
      }
      buf[c++] = val;
      sum += val;
      if (val < lo) lo = val;
      if (val > hi) hi = val;
    }
    out.cnt[k] = c;
    out.clip[k] = bits;
    if (c === 0) {
      out.v[k] = out.sd[k] = out.lo[k] = out.hi[k] = NaN;
      continue;
    }
    const mean = sum / c;
    let ss = 0;
    for (let b = 0; b < c; b++) ss += (buf[b] - mean) ** 2;
    out.sd[k] = c > 1 ? Math.sqrt(ss / (c - 1)) : 0;
    out.lo[k] = lo;
    out.hi[k] = hi;
    switch (s.stat) {
      case 'mean':
        out.v[k] = mean;
        break;
      case 'median':
        out.v[k] = median(buf, c);
        break;
      case 'min':
        out.v[k] = lo;
        break;
      case 'max':
        out.v[k] = hi;
        break;
      case 'sum':
        out.v[k] = sum;
        break;
    }
  }
  return out;
}
